import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ALL_JOB_STATUSES,
  apiTypes,
  listReportJobs,
} from "../../../api/routes";
import { WorkStatus } from "../../../client";
import { jobKeys } from "../queryKeys";
import { createBatcher } from "./batcher";

const reportJobsBatcher = createBatcher(async (reportIds: string[]) => {
  const jobs = await listReportJobs(reportIds, ALL_JOB_STATUSES, false);
  const map = new Map<string, apiTypes.ReportJobBatchEntry[]>();
  for (const job of jobs) {
    const existing = map.get(job.report_id) ?? [];
    existing.push(job);
    map.set(job.report_id, existing);
  }
  return map;
}, 50);

export function useReportJobs(
  reportId: string,
  options?: { integrationTypes?: string[] }
) {
  const { data: allJobs = [], isPending } = useQuery({
    queryKey: jobKeys.reportJobs(reportId),
    queryFn: () => reportJobsBatcher.load(reportId).then((jobs) => jobs ?? []),
    refetchInterval: 5000,
  });

  const filter = options?.integrationTypes;
  const jobs = useMemo(
    () =>
      filter
        ? allJobs.filter((j) => filter.includes(j.integration_type))
        : allJobs,
    [allJobs, filter]
  );

  return { jobs, isPending };
}

// An ops view: it reports on everything the workers are working through.
export function useQueueJobs(reportIds: string[], statuses: WorkStatus[]) {
  return useQuery({
    queryKey: jobKeys.queueJobs(reportIds),
    queryFn: () => listReportJobs(reportIds, statuses, true),
    enabled: reportIds.length > 0,
    refetchInterval: 5000,
  });
}
